<template>
  <Dialog
    @submit="onSubmit"
    :title="showSpinner ? '' : title"
    :disableButtons="disableAll || !!showSpinner"
    :loading="loadingSaveBtn"
  >
    <div v-if="showSpinner" class="spinner-wrap">
      <spinner/>
    </div>
    <form
      v-else
      @submit.prevent="onSubmit"
    >
      <div class="container">
        <div class="row" v-if="isRepresentative">
          <div class="col-6">
            <div class="form-group">
              <label for="companyName"><b>Company Name:</b></label>
              <select v-model="companyId" class="form-control" id="companyName">
                <option selected value="0">All</option>
                <option v-for="company of companies" :value="company.id" :key="company.id">{{company.companyName}}</option>
              </select>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col">
            <div class="form-check">
              <label class="form-check-label">
                <input
                  type="checkbox"
                  :checked="isAllChecked"
                  class="form-check-input"
                  @click="toggleAll"
                  :disabled="disableAll"
                >
                <b>Campaign Name</b>
              </label>
            </div>
          </div>
          <div class="col" v-if="isRepresentative">
            <b>Company Name</b>
          </div>
        </div>
      </div>
      <div class="container items">
        <div class="row" v-for="item of items" :key="item.id">
          <div class="col">
            <div class="form-check">
              <label class="form-check-label">
                <input
                  :disabled="disableAll"
                  type="checkbox"
                  :value="item.id"
                  class="form-check-input"
                  v-model="checkedItems"
                >
                {{ item.campaignName }}
              </label>
            </div>
          </div>
          <div class="col" v-if="isRepresentative">
            {{ item.companyName }}
          </div>
        </div>
      </div>
    </form>
  </Dialog>
</template>

<script>
import { uniq } from 'lodash';
import Dialog from '@/components/Dialog';
import Spinner from '@/components/Spinner';
import ModalMixin from '@/mixins/ModalMixin';

import ASSIGNABLE_CAMPAIGNS from '@/graphql/Campaigns/AssignableCampaigns.gql';
import ASSIGNED_CAMPAIGNS from '@/graphql/Campaigns/AssignedCampaigns.gql';
import ASSIGN_CAMPAIGNS from '@/graphql/Campaigns/AssignCampaigns.gql';
import COMPANIES from '@/graphql/Companies/Companies.gql';
import USER_ROLE from '@/graphql/Users/UserRole.gql';
import USER from '@/graphql/Users/User.gql';

export default {
  name: 'assign-component',
  mixins: [ModalMixin],
  components: {
    Dialog,
    Spinner,
  },
  props: {
    userId: Number,
  },

  data() {
    return {
      items: [],
      checkedItems: [],
      companies: [],
      roles: [],
      companyId: 0,
      user: {},
      showSpinner: 0,
      disableAll: false,
      loadingSaveBtn: false,
    };
  },

  computed: {
    title() {
      if (!this.user.role) {
        return '';
      }
      const {
        name,
        role: { role_type },
      } = this.user;
      const capitalizedRole = role_type[0].toUpperCase() + role_type.slice(1);

      return name && capitalizedRole
        ? `Assign Campaign to ${name} (role '${capitalizedRole}')`
        : '';
    },
    isRepresentative() {
      if (!this.user.role) {
        return false;
      }
      return this.user.role.role_type === 'representative';
    },
    isAllChecked() {
      return this.checkedItems.length && this.checkedItems.length === this.items.length;
    },
    clientRole() {
      return this.roles.find(role => role.role_type === 'client');
    },
  },

  methods: {
    onSubmit() {
      this.disableAll = true;
      this.loadingSaveBtn = true;

      this.$apollo
        .mutate({
          mutation: ASSIGN_CAMPAIGNS,
          variables: {
            userId: this.userId,
            campaignIds: this.checkedItems.map(item => parseInt(item)),
          },
        })
        .then(() => {
          this.close();

          this.$notify({
            group: 'success',
            type: 'success',
            text: 'Campaign was assigned',
          });
        })
        .catch(e => {
          this.$notify({
            group: 'error',
            type: 'error',
            text: e.message,
          });
        })
        .finally(() => {
          this.disableAll = false;
          this.loadingSaveBtn = false;
        });
    },

    toggleAll() {
      if (this.isAllChecked) {
        // uncheck
        this.checkedItems = [];
      } else {
        this.checkedItems = this.items.map(item => item.id);
      }
    },
  },

  apollo: {
    assignableCampaigns: {
      query: ASSIGNABLE_CAMPAIGNS,
      loadingKey: 'showSpinner',
      fetchPolicy: 'network-only',
      result({ data, loading }) {
        if (!loading) {
          this.items = data.assignableCampaigns;
        }
      },
      variables() {
        const query = {};
        if (this.isRepresentative) {
          query.companyId = this.companyId;
        } else {
          query.userId = this.userId;
        }
        return query;
      },
    },

    assignedCampaigns: {
      query: ASSIGNED_CAMPAIGNS,
      loadingKey: 'showSpinner',
      fetchPolicy: 'network-only',
      result({ data, loading }) {
        if (!loading) {
          this.checkedItems = uniq(data.assignedCampaigns.map(item => item.id));
        }
      },
      variables() {
        return {
          userId: this.userId,
        };
      },
    },

    roles: {
      query: USER_ROLE,
      fetchPolicy: 'network-only',
      result({ data, loading }) {
        if (!loading) {
          this.roles = data.roles;
        }
      },
    },

    user: {
      query: USER,
      fetchPolicy: 'network-only',
      result({ data, loading }) {
        if (!loading) {
          this.user = data.user;
        }
      },
      variables() {
        return {
          id: this.userId,
        };
      },
    },

    companies: {
      query: COMPANIES,
      fetchPolicy: 'network-only',
      result({ data, loading }) {
        if (!loading) {
          this.companies = data.companies.result;
        }
      },
      variables() {
        return {
          limit: -1,
          status: true,
        };
      },
      skip() {
        return !this.isRepresentative || !this.clientRole;
      },
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.container.items {
  max-height: 314px;
  overflow: auto;
  .row {
    border-top: 1px solid #e9ecef;
    padding-top: 0.4rem;
  }
}
b {
  font-weight: 500;
}
label {
  font-weight: 300;
}
.spinner-wrap {
  position: relative;
  height: 100px;
  align-items: center;
}
</style>



// WEBPACK FOOTER //
// src/modals/AssignCampaign.vue